import { AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StockLogo } from "@/components/stock-logo";
import { formatCurrency } from "@/lib/format";
import type { DividendPositionAnomalies } from "@/lib/dividend-tracker/load-projection";

function formatAnomalyText(position: DividendPositionAnomalies): string[] {
  return position.anomalies.map((a) => {
    if (a.type === "cut") {
      return (
        `Zuletzt ${formatCurrency(a.lastAmountPerShare, position.currency, 3)} je Aktie ` +
        `statt ${formatCurrency(a.comparisonAmountPerShare, position.currency, 3)} im Vorjahreszeitraum ` +
        `(${a.comparisonDate.toLocaleDateString("de-DE")}) - möglicherweise eine Kürzung.`
      );
    }
    return (
      `Nächste Zahlung wurde für ca. ${a.expectedDate.toLocaleDateString("de-DE")} erwartet, ` +
      `ist aber in den Marktdaten noch nicht aufgetaucht - möglicherweise verzögert, ausgesetzt ` +
      `oder die Datenquelle ist noch nicht aktuell.`
    );
  });
}

/**
 * Dividenden-Prognose-Abgleich (backlog.md -> "Weiterer Nutzwert-Katalog" -> Dividenden,
 * "Abgleich prognostizierte vs. tatsächlich eingegangene Dividende") - vergleicht die
 * ECHTE, marktweite Zahlungshistorie jeder Depot-Position gegen ihr eigenes bisheriges
 * Muster (siehe lib/dividend-tracker/anomaly-detection.ts), NICHT gegen die eigenen
 * manuell erfassten dividend_events - dadurch entsteht kein Fehlalarm nur weil der Nutzer
 * eine Zahlung noch nicht selbst eingetragen hat. Gleiches "Konzentrationshinweise"-Muster
 * wie etf-composition-section.tsx (Gold-Akzent statt einer neuen Warnfarbe). Rendert
 * nichts bei einem unauffälligen Depot.
 */
export function DividendAnomalyAlerts({ positions }: { positions: DividendPositionAnomalies[] }) {
  if (positions.length === 0) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <AlertTriangle className="h-4 w-4 text-gold-text" aria-hidden="true" />
          Auffälligkeiten bei Dividendenzahlungen
        </div>
        <ul className="flex flex-col gap-3">
          {positions.map((position) => (
            <li key={position.symbol} className="flex flex-col gap-1 rounded-lg border border-gold/40 bg-gold/[0.03] p-3">
              <div className="flex items-center gap-2">
                <StockLogo symbol={position.symbol} logoUrl={position.logoUrl} size={20} />
                <span className="text-sm font-medium text-ink">{position.name}</span>
              </div>
              <ul className="flex flex-col gap-0.5 pl-7 text-xs text-ink-secondary">
                {formatAnomalyText(position).map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          Basiert auf der Zahlungshistorie der jeweiligen Aktie im Vergleich zu ihrem eigenen
          bisherigen Muster - rein deskriptiv, keine Bewertung und keine Anlageempfehlung. Kann
          auch schlicht eine noch nicht aktualisierte Datenquelle statt einer echten Kürzung sein.
        </p>
      </CardContent>
    </Card>
  );
}
